import { Box, Button, Container, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminOrders from './tabs/Orders';
import AdminProducts from './tabs/Products';
import NotifySubscribers from './tabs/NotifySubscribers';

const ADMIN_TAB_PATHS = ['/admin/orders', '/admin/products', '/admin/email-list'] as const;

function tabIndexFromPath(pathname: string): number {
  const p = pathname.replace(/\/$/, '') || '/admin';
  if (p === '/admin/products') return 1;
  if (p === '/admin/email-list' || p === '/admin/notify-subscribers') return 2;
  return 0;
}

const Admin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tabIndex, setTabIndex] = useState(() => tabIndexFromPath(location.pathname));

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

  useEffect(() => {
    const storedPassword = localStorage.getItem('adminPassword');
    if (storedPassword === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    }
  }, [ADMIN_PASSWORD]);

  useEffect(() => {
    setTabIndex(tabIndexFromPath(location.pathname));
  }, [location.pathname]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    navigate(ADMIN_TAB_PATHS[newValue]);
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      localStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password!');
    }
  };

  if (!isAuthenticated) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ bgcolor: 'action.hover' }}
      >
        <Paper elevation={0} sx={{ p: 4, maxWidth: 400, width: '100%', mx: 2, border: 1, borderColor: 'divider' }}>
          <Typography variant="h5" component="h1" fontWeight={600} gutterBottom>
            Admin sign in
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Enter the admin password to continue.
          </Typography>
          <TextField
            fullWidth
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.code === "Enter") handleLogin();
            }}
            margin="normal"
            autoComplete="current-password"
          />
          <Button variant="contained" fullWidth disabled={!password.length} onClick={handleLogin} sx={{ mt: 2 }}>
            Log in
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'action.hover', minHeight: '100vh', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Admin
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Orders, products, and email list
          </Typography>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="admin tabs"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 0,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            }}
          >
            <Tab label="Orders" />
            <Tab label="Products" />
            <Tab label="Email list" />
          </Tabs>
          <Box sx={{ pt: 3 }}>
            {tabIndex === 0 && <AdminOrders />}
            {tabIndex === 1 && <AdminProducts />}
            {tabIndex === 2 && <NotifySubscribers />}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Admin;
